import type {StateCreator} from 'zustand'
import type {MarkersInfo, MarkersSlice} from './markers'
import {
  getSelectedAnnotationId,
  getSelectedCommentId,
  parsePathDigestWithoutLineNumbers,
} from '@github-ui/diffs/document-hash-helpers'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'
import type {ProgressiveDiffsSlice} from './progressive-diffs'
import type {DiffFilterSlice} from './diff-filter'
import {produce} from 'immer'
import type {FilesCollapsedStatusSlice} from './files-collapsed-status'
import {getPathOwnership} from '../../page-data/loaders/use-codeowners-data'
import type {CodeownersSlice} from './codeowners'
import {clearDiffFilterSearchParams, matchesDiffFilters} from '../../helpers/diff-helpers'
import {updateSearchParams} from '@github-ui/history'
export interface MarkerAndDiffNavigationSlice {
  markerAndDiffNavigation: {
    markerId: string | undefined
    commentId: number | undefined // check to see if string
    getMarkerId: () => string | undefined
    getCommentId: () => number | undefined
  }
  markerAndDiffNavigationActions: {
    navigateToSelectedComment: (commentId: number) => void
    navigateToSelectedAnnotation: (annotationId: number) => void
    handleHashUpdate: () => void
  }
}

export interface MarkerAndDiffNavigationOpts {
  markers?: MarkersInfo | (undefined & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>)
}

type SliceState = MarkerAndDiffNavigationSlice &
  DiffSummariesSlice &
  MarkersSlice &
  ProgressiveDiffsSlice &
  DiffFilterSlice &
  CodeownersSlice

function isThreadHiddenByCurrentFilters(state: SliceState, threadID: string): boolean {
  const diff = state.getDiffFromThreadID(threadID)
  if (!diff) return false

  const filters = state.diffFilter.state
  const isOwnedByViewer = getPathOwnership({
    diffPath: diff.path,
    codeownersData: state.codeowners,
  }).isOwnedByViewer

  return !matchesDiffFilters(diff, filters, isOwnedByViewer)
}

export const createMarkerAndDiffNavigationSlice =
  ({
    markers, // Markers are passed here to set the initial values. The store is not yet initialized.
  }: MarkerAndDiffNavigationOpts): StateCreator<
    SliceState & FilesCollapsedStatusSlice,
    [['zustand/devtools', never]],
    [],
    MarkerAndDiffNavigationSlice
  > =>
  (set, get) => {
    const selectedCommentId = getSelectedCommentId()
    const selectedAnnotationId = getSelectedAnnotationId()
    let markerId = undefined
    let commentId = undefined

    if (markers) {
      if (selectedCommentId) {
        const threads = Object.values(markers || {}).flatMap(files =>
          Object.values(files).flatMap(threadsInLine => {
            return threadsInLine?.threads ?? []
          }),
        )
        if (threads) {
          const selectedCommentIdNum = Number(selectedCommentId)
          for (const thread of Object.values(threads)) {
            const comments = thread?.commentsData?.comments
            if (thread && comments) {
              for (const comment of comments) {
                if (Number(comment.databaseId) === selectedCommentIdNum) {
                  markerId = thread.id
                  commentId = selectedCommentIdNum
                }
              }
            }
          }
        }
      } else if (selectedAnnotationId) {
        const annotations = Object.values(markers).flatMap(files =>
          Object.values(files).flatMap(annotationsInLine => {
            return annotationsInLine?.annotations ?? []
          }),
        )
        if (annotations) {
          const selectedAnnotationIdNum = Number(selectedAnnotationId)
          for (const annotation of Object.values(annotations)) {
            if (Number(annotation.databaseId) === selectedAnnotationIdNum) {
              markerId = annotation.id
            }
          }
        }
      }
    }
    return {
      markerAndDiffNavigation: {
        markerId,
        commentId,
        getCommentId: () => get().markerAndDiffNavigation.commentId,
        getMarkerId: () => get().markerAndDiffNavigation.markerId,
      },
      markerAndDiffNavigationActions: {
        navigateToSelectedComment: (targetCommentId: number) => {
          const thread = get().getThreadFromCommentID(targetCommentId)
          if (thread) {
            const threadPathDigest = get().getPathDigestFromThreadID(thread.id)

            // combining updates from two slices into one call to prevent re-render flickering
            set(
              produce<SliceState & FilesCollapsedStatusSlice>(store => {
                if (threadPathDigest) {
                  // updates the progressive-diff slice
                  store.selectedPathDigest = threadPathDigest
                }

                // updates marker-diff-navigation slice
                store.markerAndDiffNavigation.commentId = targetCommentId
                store.markerAndDiffNavigation.markerId = thread?.id
              }),
              undefined,
              'navigateToSelectedComment',
            )

            // Check and clear filters to show files if needed
            if (isThreadHiddenByCurrentFilters(get(), thread.id)) {
              get().diffFilterActions.clearAllFilters()
              updateSearchParams(clearDiffFilterSearchParams(new URLSearchParams(window.location.search)))
            }

            // Check and uncollapse file if needed
            const filePath = get().getFilePathFromThreadID(thread.id)
            if (filePath && get().getCollapsedDiffStatus(filePath)) {
              get().filesCollapsedStatusActions.updateFileCollapsedStatus(filePath, false)
            }

            // diff loader filters out any previous loaded digests
            get().progressiveDiffsActions.loadPriority(threadPathDigest)
          }
        },
        navigateToSelectedAnnotation: (annotationId: number) => {
          const annotation = get().getAnnotationFromID(annotationId)

          if (annotation) {
            const annotationPathDigest = get().getPathDigestFromAnnotationID(annotationId)

            // combining updates from two slices into one call to prevent re-render flickering
            set(
              produce<SliceState>(store => {
                if (annotationPathDigest) {
                  // updates the progressive-diff slice
                  store.selectedPathDigest = annotationPathDigest
                }

                // updates marker-diff-navigation slice
                store.markerAndDiffNavigation.commentId = undefined
                store.markerAndDiffNavigation.markerId = annotation.id
              }),
              undefined,
              'navigateToSelectedAnnotation',
            )

            // diff loader filters out any previous loaded digests
            get().progressiveDiffsActions.loadPriority(annotationPathDigest)
          }
        },
        handleHashUpdate: () => {
          const urlCommentId = getSelectedCommentId()
          const urlAnnotationId = getSelectedAnnotationId()
          const urlPathDigest = parsePathDigestWithoutLineNumbers(window.location.hash)

          const currentState = get()
          const currentCommentId = currentState.markerAndDiffNavigation.commentId
          const currentMarkerId = currentState.markerAndDiffNavigation.markerId
          const currentPathDigest = currentState.selectedPathDigest

          // Navigate based on URL hash values, only if something has changed to avoid rerenders
          // Also navigate if we have a commentId but no selectedPathDigest (can happen on initial page load
          // where store initialization sets commentId from URL but doesn't set selectedPathDigest)
          if (urlCommentId && (urlCommentId !== currentCommentId || !currentPathDigest)) {
            currentState.markerAndDiffNavigationActions.navigateToSelectedComment(urlCommentId)
          } else if (urlAnnotationId) {
            // Navigate to the selected annotation
            const annotation = currentState.getAnnotationFromID(urlAnnotationId)
            const expectedMarkerId = annotation?.id

            if (expectedMarkerId && expectedMarkerId !== currentMarkerId) {
              currentState.markerAndDiffNavigationActions.navigateToSelectedAnnotation(urlAnnotationId)
            }
          } else if (urlPathDigest && urlPathDigest !== currentPathDigest) {
            // Navigate to the selected path digest
            currentState.progressiveDiffsActions.updateSelectedPathDigest(urlPathDigest)
          } else if (!urlCommentId && !urlAnnotationId && !urlPathDigest && currentPathDigest) {
            // Clear selection if no hash is present but we have a current selection
            currentState.progressiveDiffsActions.updateSelectedPathDigest(undefined)
          }
        },
      },
    }
  }
